import { z } from "zod";
const id = z.string().uuid();
const revision = z.number().int().positive();
const count = z.number().int().min(1).max(1000);
export const rectangle = z
    .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().positive().max(1),
    height: z.number().positive().max(1),
})
    .refine((r) => r.x + r.width <= 1.001 && r.y + r.height <= 1.001, "Rectangle must fit the image");
export const block = z
    .object({
    key: id,
    productId: id.nullable(),
    productName: z.string().max(500),
    order: count,
    facings: count,
    // facings remains the visible-unit total. Missing layout on old snapshots is
    // unknown, never an implicit single row. Columns always read left to right.
    horizontalFacings: count.nullable().optional(),
    stackHeights: z.array(count).min(1).max(1000).nullable().optional(),
    bounds: rectangle.nullable(),
    evidence: z.array(z.number().int().nonnegative()).max(1000).default([]),
    reviewColumn: z
        .object({
        reason: z.enum(["mixed_stack", "unresolved_stack"]),
        detections: z
            .array(z.object({
            detectionId: z.number().int().nonnegative(),
            productId: id.nullable(),
            productName: z.string().max(500),
            bounds: rectangle,
        }))
            .min(1)
            .max(1000),
    })
        .nullable()
        .optional(),
})
    .superRefine((b, ctx) => {
    if (b.reviewColumn && (b.productId || b.stackHeights || b.horizontalFacings != null))
        ctx.addIssue({
            code: "custom",
            message: "Resolve the review column before assigning product counts",
        });
    if (b.horizontalFacings != null && b.horizontalFacings > b.facings)
        ctx.addIssue({
            code: "custom",
            path: ["horizontalFacings"],
            message: "Columns cannot exceed visible units",
        });
    if (b.stackHeights &&
        (b.stackHeights.length !== b.horizontalFacings ||
            b.stackHeights.reduce((n, h) => n + h, 0) !== b.facings))
        ctx.addIssue({
            code: "custom",
            path: ["stackHeights"],
            message: "Provide one height per column; their sum must equal visible units",
        });
});
export const shelf = z.object({
    key: id,
    name: z.string().trim().min(1).max(100),
    order: count,
    physicalShelfId: id.nullable(),
    bounds: rectangle.nullable(),
    reviewed: z.boolean(),
    blocks: z.array(block).max(500),
});
export const arrangement = z
    .object({
    shelves: z.array(shelf).max(50),
    fullFixture: z.boolean(),
})
    .superRefine((a, ctx) => {
    const keys = new Set();
    a.shelves.forEach((s, i) => {
        if (s.order !== i + 1)
            ctx.addIssue({
                code: "custom",
                message: "Shelf order must be consecutive",
                path: ["shelves", i, "order"],
            });
        for (const [j, b] of s.blocks.entries()) {
            if (b.order !== j + 1)
                ctx.addIssue({
                    code: "custom",
                    message: "Product order must be consecutive",
                    path: ["shelves", i, "blocks", j, "order"],
                });
        }
        for (const key of [s.key, ...s.blocks.map((b) => b.key)]) {
            if (keys.has(key))
                ctx.addIssue({ code: "custom", message: "Shelf and block keys must be unique" });
            keys.add(key);
        }
    });
});
export const publishableArrangement = arrangement.refine((a) => a.fullFixture &&
    a.shelves.length > 0 &&
    a.shelves.every((s) => s.reviewed &&
        s.bounds &&
        s.blocks.every((b) => b.productId && b.stackHeights && !b.reviewColumn)), "Review the complete fixture, shelf boundaries, product identities and stack heights before saving the planogram");
export const referenceInfo = z.object({
    attemptId: id.nullable(),
    status: z.enum(["none", "queued", "processing", "ready", "failed", "stale"]),
    imageUrl: z.string().nullable(),
    error: z.string().nullable(),
    proposal: arrangement.nullable(),
    provider: z.string().nullable(),
    needsReview: z.boolean().optional(),
    warnings: z.array(z.object({ stage: z.string(), message: z.string() })).optional(),
});
export const versionInfo = z.object({
    id,
    state: z.enum(["draft", "published"]),
    number: revision.nullable(),
    revision,
    baseVersionId: id.nullable(),
    categoryName: z.string().nullable(),
    arrangement,
    sourceImageUrl: z.string().nullable(),
    sourceCaptureId: id.nullable(),
    reference: referenceInfo,
    note: z.string(),
    createdAt: z.string(),
    publishedAt: z.string().nullable(),
    publishedBy: z.string().nullable(),
});
export const summary = z.object({
    state: z.enum(["none", "draft", "active", "active_with_draft"]),
    version: revision.nullable(),
    updatedAt: z.string().nullable(),
});
export const workspace = z.object({
    id: id.nullable(),
    fixtureId: id,
    fixtureCode: z.string(),
    fixtureName: z.string().nullable(),
    storeId: id,
    categoryName: z.string().nullable(),
    face: z.literal("front"),
    canEdit: z.boolean(),
    active: versionInfo.nullable(),
    draft: versionInfo.nullable(),
});
export const startDraftRequest = z.object({}).strict();
export const updateDraftRequest = z
    .object({ expectedRevision: revision, arrangement, note: z.string().max(1000).default("") })
    .strict();
export const activateRequest = z
    .object({
    expectedRevision: revision,
    baseVersionId: id.nullable(),
    requestId: id,
    note: z.string().max(1000).default(""),
})
    .strict();
export const referenceRequest = z
    .object({ expectedRevision: revision, captureId: id, visionRunId: id })
    .strict();
export const acceptReferenceRequest = z
    .object({ expectedRevision: revision, attemptId: id })
    .strict();
// `versionId` is optional: omitted compares against the active (latest) version;
// set, it compares against that specific published version on demand.
export const compareRequest = z
    .object({ visionRunId: id, requestId: id, versionId: id.optional() })
    .strict();
export const revisionRequest = z.object({ expectedRevision: revision }).strict();
export const shelfConditionKind = z.enum(["empty_space", "back_facing", "side_facing"]);
/** Capture evidence, independent of product counts or baseline placement. */
export const shelfCondition = z.object({
    kind: shelfConditionKind,
    shelfKey: id.nullable(),
    productId: id.nullable(),
    productName: z.string(),
    visibleUnits: z.number().int().positive().nullable(),
    bounds: rectangle.nullable(),
    evidence: z.array(z.number().int().nonnegative()),
});
export const finding = z.object({
    kind: z.enum([
        "count",
        "horizontal_facings",
        "stack",
        "order",
        "missing",
        "unexpected",
        "wrong_shelf",
        "split_block",
        "unable_to_assess",
        ...shelfConditionKind.options,
    ]),
    shelfKey: id,
    shelfName: z.string(),
    blockKey: id.nullable(),
    productId: id.nullable(),
    productName: z.string(),
    message: z.string(),
    expected: z.number().int().nonnegative().nullable(),
    observed: z.number().int().nonnegative().nullable(),
    expectedStackHeights: z.array(count).max(1000).optional(),
    observedStackHeights: z.array(count).max(1000).optional(),
    stackUnitDifferences: z.number().int().nonnegative().optional(),
    evidence: z.array(z.number().int().nonnegative()),
    /** Exact capture region for condition findings; absent in older results. */
    observedBounds: rectangle.nullable().optional(),
});
// A single, un-versioned comparator. Results pin this string so a comparison
// produced by an earlier rule can be recognised and offered "Compare with
// latest"; bump it only when a logic change must mark older results stale.
export const COMPARISON_RULE_VERSION = "fixture-planogram";
export const comparedShelf = z.object({
    /** Normalized photo coordinates, from the pinned observation (never fixture ordinal). */
    observedBounds: rectangle.nullable().optional(),
    shelfKey: id,
    shelfName: z.string(),
    order: count,
    observedOrder: count.nullable(),
    assessed: z.boolean(),
    expectedBlocks: z.array(block),
    // null means unmapped, not an empty shelf or missing products.
    observedBlocks: z.array(block).nullable(),
});
export const result = z.object({
    assessment: z.enum(["matches", "differences", "needs_review"]),
    coverage: z.enum(["full", "partial"]),
    assessedShelves: z.number().int().nonnegative(),
    totalShelves: z.number().int().nonnegative(),
    expectedFacings: z.number().int().nonnegative(),
    observedFacings: z.number().int().nonnegative(),
    uncertainFacings: z.number().int().nonnegative(),
    findings: z.array(finding),
    // Optional for immutable results produced before v3.
    shelves: z.array(comparedShelf).optional(),
});
export const comparison = z
    .object({
    id,
    visionRunId: id,
    status: z.enum(["queued", "processing", "completed", "failed", "skipped"]),
    versionId: id.nullable(),
    versionNumber: revision.nullable(),
    ruleVersion: z.string().nullable(),
    reason: z.string().nullable(),
    result: result.nullable(),
    createdAt: z.string(),
    completedAt: z.string().nullable(),
})
    .superRefine((c, ctx) => {
    if (c.status === "completed" &&
        (!c.result || !c.versionId || !c.versionNumber || !c.ruleVersion))
        ctx.addIssue({
            code: "custom",
            message: "Completed comparisons must pin a baseline and result",
        });
    if (c.status === "skipped" && (!c.reason || c.result || c.versionId))
        ctx.addIssue({
            code: "custom",
            message: "Skipped comparisons have a reason, no baseline and no result",
        });
});
export const captureStatus = z.object({
    captureId: id,
    fixtureId: id.nullable(),
    // The fixture-planogram root, so the UI can list its versions to compare against.
    planogramId: id.nullable(),
    visionRunId: id.nullable(),
    activeVersion: revision.nullable(),
    status: z.enum(["waiting", "not_evaluated", "available"]),
    reason: z.string().nullable(),
    canEdit: z.boolean(),
    canCompare: z.boolean(),
    comparison: comparison.nullable(),
});
export const historyQuery = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(50).default(20),
});
export const versionHistory = z.object({
    items: z.array(versionInfo),
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
});
export const comparisonHistory = z.object({
    items: z.array(comparison),
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
});
export const jobPayload = z.object({ id }).strict();
/** Read-only scan report rows: no raw Vision, photos or layout snapshots. */
export const scanComparison = z.object({
    captureId: id,
    capturedAt: z.string(),
    fixtureName: z.string().nullable(),
    fixtureCode: z.string().nullable(),
    categoryName: z.string().nullable(),
    zoneName: z.string().nullable(),
    activeVersion: revision.nullable(),
    status: z.enum([
        "waiting",
        "not_evaluated",
        "queued",
        "processing",
        "completed",
        "failed",
        "skipped",
    ]),
    reason: z.string().nullable(),
    versionNumber: revision.nullable(),
    ruleVersion: z.string().nullable(),
    comparedAt: z.string().nullable(),
    result: result.omit({ shelves: true }).nullable(),
});
export const scanComparisons = z.object({
    items: z.array(scanComparison),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
});
//# sourceMappingURL=fixture-planograms.schemas.js.map